"use client";

import { forwardRef } from "react";
import {
  Box,
  Button,
  Dialog,
  Flex,
  Heading,
  IconButton,
} from "@radix-ui/themes";
import FontAwesome from "../../components/utilities/FontAwesome";
import { useT } from "../../components/utilities/TranslatedText";
import styles from "./PageIndexPage.module.scss";
import PageIndexSidebarFilters from "./PageIndexSidebarFilters";
import { PublishedFilterValue } from "./PageIndexPageClient";

type PageIndexSidebarProps = {
  letters: string[];
  activeLetter: string | null;
  onLetterClick: (letter: string) => void;
  disabled: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;

  publishedFilter: PublishedFilterValue;
  onPublishedChange: (value: PublishedFilterValue) => void;
  showPublishedFilter: boolean;

  onClearAll: () => void;
  resultCount: number;

  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
};

const PageIndexSidebar = forwardRef<HTMLElement, PageIndexSidebarProps>(
  (
    {
      letters,
      activeLetter,
      onLetterClick,
      disabled,
      searchTerm,
      onSearchChange,
      publishedFilter,
      onPublishedChange,
      showPublishedFilter,
      onClearAll,
      resultCount,
      mobileOpen,
      onMobileOpenChange,
    },
    ref,
  ) => {
    const t = useT();
    const sharedFilterProps = {
      searchTerm,
      onSearchChange,
      showPublishedFilter,
      publishedFilter,
      onPublishedChange,
      letters,
      activeLetter,
      onLetterClick,
      disabled,
    };

    return (
      <>
        <Box
          asChild
          className={styles.sidebarDesktop}
          display={{ initial: "none", sm: "block" }}
        >
          <aside ref={ref} aria-label={t("Page directory filters")}>
            <Flex align="baseline" justify="between" mb="4">
              <Heading as="h2" size="2">
                {t("Filters")}
              </Heading>
              <Button variant="ghost" size="1" onClick={onClearAll}>
                {t("Clear all")}
              </Button>
            </Flex>
            <PageIndexSidebarFilters
              idPrefix="desktop"
              {...sharedFilterProps}
            />
          </aside>
        </Box>

        <Dialog.Root open={mobileOpen} onOpenChange={onMobileOpenChange}>
          <Dialog.Content maxWidth="420px">
            <Flex justify="between" align="center">
              <Dialog.Title>{t("Filters")}</Dialog.Title>
              <Dialog.Close>
                <IconButton variant="ghost" aria-label={t("Close filters")}>
                  <FontAwesome prefix="fas" name="fa-xmark" ariaHidden />
                </IconButton>
              </Dialog.Close>
            </Flex>
            <Dialog.Description size="2" color="gray" mb="4">
              {t(
                "Filter the page directory by search term, published status, and starting letter.",
              )}
            </Dialog.Description>
            <PageIndexSidebarFilters idPrefix="mobile" {...sharedFilterProps} />
            <Flex gap="3" mt="5" justify="end">
              <Button variant="surface" onClick={onClearAll}>
                {t("Clear all")}
              </Button>
              <Button onClick={() => onMobileOpenChange(false)}>
                {`${t("Show")} ${resultCount} ${t(resultCount === 1 ? "page" : "pages")}`}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </>
    );
  },
);

PageIndexSidebar.displayName = "PageIndexSidebar";

export default PageIndexSidebar;
